// Simulateur local de l'API terrain (contrat packages/contrats/src/terrain.ts) : utilisé par les tests,
// par l'aperçu hors production et par les parcours de recette sans serveur. Il tient « l'état du
// serveur » dans le stockage local du navigateur (survit aux rechargements), applique les règles
// d'idempotence et de conflit de D-041 et la règle de version D-130 (une opération de l'agent ne change
// jamais la version ; seules la réaffectation et la clôture la font avancer), pagine GET /terrain/travail
// comme le serveur, refuse toute requête hors connexion et expose des actions de recette (réaffectation,
// clôture) pour R9. Jeu de données fictif de présentation.
import type { MissionTerrain, OperationTerrain, OrdreMissionTerrain, RealisationTournee, ReponsePhotoTerrain, ReponseSynchronisationTerrain, ResultatOperationTerrain, SaisieConstat, SynchronisationTerrain, TourneeTerrain, TravailTerrain } from '@cc/contrats';
import { ErreurApi } from '../api/erreurs';
import type { ApiTerrain, CurseursTravail } from './api';
import type { EnvoiPhoto } from './appareil';
import { jeuDemonstration } from './simulateur-jeu';

export { jeuDemonstration };

export const CLE_ETAT_SIMULATEUR = 'cc-terrain:simulateur:etat';
export const CLE_LATENCE_SIMULATEUR = 'cc-terrain:simulateur:latence';

export interface EtatSimulateur {
  cree_le: string;
  agent: TravailTerrain['agent'];
  missions: MissionTerrain[];
  tournees: TourneeTerrain[];
  ordres_mission: OrdreMissionTerrain[];
  referentiels: TravailTerrain['referentiels'];
  /** Résultats déjà rendus par identifiant client (idempotence). */
  journal: Record<string, ResultatOperationTerrain>;
  photos: Record<string, ReponsePhotoTerrain>;
  /** Dernier changement fait « côté serveur » sur un objet (motif des conflits). */
  modifications: Record<string, string>;
  evenements: string[];
}

export interface OptionsSimulateur {
  /** Délai simulé par requête (ms) ; 0 dans les tests. */
  latenceMs?: number;
  stockage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  enLigne?: () => boolean;
  horloge?: () => Date;
}

function copie<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

/** Page d'objets triés par identifiant, après le curseur, bornée par la limite (comme le serveur). */
function paginer<T extends { id: string }>(objets: T[], apres: string | undefined, limite: number) {
  const tries = [...objets].sort((a, b) => a.id.localeCompare(b.id));
  const restants = apres ? tries.filter((o) => o.id > apres) : tries;
  const elements = restants.slice(0, limite);
  return { elements, total: objets.length, suivant: elements.length === limite && restants.length > limite ? (elements[elements.length - 1]?.id ?? null) : null };
}

export class SimulateurTerrain implements ApiTerrain {
  readonly source = 'simulateur' as const;
  private memoire: EtatSimulateur | null = null;
  private readonly latence: number;
  private readonly stockage: OptionsSimulateur['stockage'];
  private readonly enLigne: () => boolean;
  private readonly horloge: () => Date;

  constructor(options: OptionsSimulateur = {}) {
    this.stockage = options.stockage === undefined ? (typeof window !== 'undefined' ? window.localStorage : null) : options.stockage;
    // Latence simulée : réglable par la recette (clé cc-terrain:simulateur:latence, en ms) pour rendre visibles les états d'attente.
    const reglee = Number(this.stockage?.getItem(CLE_LATENCE_SIMULATEUR) ?? NaN);
    this.latence = options.latenceMs ?? (Number.isFinite(reglee) ? reglee : 150);
    this.enLigne = options.enLigne ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
    this.horloge = options.horloge ?? (() => new Date());
  }

  /** État « serveur » courant (copie). */
  etat(): EtatSimulateur {
    return copie(this.lire());
  }

  private lire(): EtatSimulateur {
    if (this.memoire) return this.memoire;
    try {
      const brut = this.stockage?.getItem(CLE_ETAT_SIMULATEUR);
      const lu = brut ? (JSON.parse(brut) as EtatSimulateur) : null;
      // Un état d'avant le raccordement (sans agent) est remplacé par le jeu courant.
      this.memoire = lu && lu.agent ? lu : null;
    } catch {
      this.memoire = null;
    }
    if (!this.memoire) {
      this.memoire = jeuDemonstration(this.horloge()) as EtatSimulateur;
      this.ecrire();
    }
    return this.memoire;
  }

  private ecrire(): void {
    if (!this.memoire) return;
    try {
      this.stockage?.setItem(CLE_ETAT_SIMULATEUR, JSON.stringify(this.memoire));
    } catch {
      // Quota dépassé ou stockage indisponible : l'état reste en mémoire pour la page.
    }
  }

  private async requete(): Promise<void> {
    if (!this.enLigne()) throw new ErreurApi(0, 'hors_ligne', 'Le serveur est injoignable : vérifiez la connexion.');
    if (this.latence > 0) await new Promise((r) => setTimeout(r, this.latence));
  }

  reinitialiser(): void {
    this.memoire = jeuDemonstration(this.horloge()) as EtatSimulateur;
    this.ecrire();
  }

  async travail(curseurs: CurseursTravail = {}): Promise<TravailTerrain> {
    await this.requete();
    const e = this.lire();
    const limite = curseurs.limite ?? 100;
    return copie({
      horodatage: this.horloge().toISOString(),
      agent: e.agent,
      missions: paginer(e.missions, curseurs.missions_apres, limite),
      tournees: paginer(e.tournees, curseurs.tournees_apres, limite),
      ordres_mission: paginer(e.ordres_mission, curseurs.ordres_apres, limite),
      referentiels: e.referentiels,
    });
  }

  async synchroniser(corps: SynchronisationTerrain): Promise<ReponseSynchronisationTerrain> {
    await this.requete();
    const e = this.lire();
    const resultats = corps.operations.map((op) => this.appliquer(e, op));
    this.ecrire();
    return { resultats, horodatage: this.horloge().toISOString() };
  }

  private appliquer(e: EtatSimulateur, op: OperationTerrain): ResultatOperationTerrain {
    const deja = e.journal[op.id_client];
    if (deja) return { id_client: op.id_client, resultat: 'ignoree_doublon', version: deja.version, objet_id: deja.objet_id };
    const objet = this.objet(e, op);
    let r: ResultatOperationTerrain;
    if (!objet) r = { id_client: op.id_client, resultat: 'rejetee', motif: `Objet ${op.objet_type} ${op.objet_id} inconnu du serveur` };
    else if (op.version_attendue !== objet.version) {
      const modification = e.modifications[op.objet_id] ?? 'objet modifié sur le serveur';
      r = { id_client: op.id_client, resultat: 'conflit', motif: `${modification} (version ${objet.version} sur le serveur, version ${op.version_attendue} attendue)`, version: objet.version, objet_id: op.id_client };
      if (op.type === 'rapport_mission' && 'rapports' in objet) {
        objet.rapports.push({ id: op.id_client, id_client: op.id_client, type: (op.charge as { type: MissionTerrain['rapports'][number]['type'] }).type, etat: 'conflit', recu_le: this.horloge().toISOString(), photos: [] });
        objet.etat = 'conflit';
      } else if (op.type === 'constat' && 'formulaire' in objet) objet.etat = 'conflit';
    } else {
      this.executer(objet, op);
      r = { id_client: op.id_client, resultat: 'appliquee', version: objet.version, objet_id: op.type === 'debut_mission' || op.type === 'position' ? op.objet_id : op.id_client };
    }
    e.journal[op.id_client] = r;
    e.evenements.push(`${this.horloge().toISOString()} ${op.type} ${op.objet_id} : ${r.resultat}`);
    return r;
  }

  private objet(e: EtatSimulateur, op: OperationTerrain): MissionTerrain | TourneeTerrain | OrdreMissionTerrain | undefined {
    if (op.objet_type === 'mission') return e.missions.find((m) => m.id === op.objet_id);
    if (op.objet_type === 'tournee') return e.tournees.find((t) => t.id === op.objet_id);
    return e.ordres_mission.find((o) => o.id === op.objet_id);
  }

  /** Une opération de l'agent ne change pas la version (D-130) : seules les modifications du bureau l'incrémentent. */
  private executer(objet: MissionTerrain | TourneeTerrain | OrdreMissionTerrain, op: OperationTerrain): void {
    const maintenant = this.horloge().toISOString();
    if (op.type === 'debut_mission' && 'rapports' in objet) {
      if (objet.etat === 'affectee') objet.etat = 'en_cours';
    } else if (op.type === 'rapport_mission' && 'rapports' in objet) {
      const charge = op.charge as { type: MissionTerrain['rapports'][number]['type']; contenu?: { termine?: boolean } };
      objet.rapports.push({ id: op.id_client, id_client: op.id_client, type: charge.type, etat: 'synchronise', recu_le: maintenant, photos: [] });
      if (charge.type === 'final' || charge.contenu?.termine) objet.etat = 'rapport_recu';
      else if (objet.etat === 'affectee') objet.etat = 'en_cours';
    } else if (op.type === 'realisation_tournee' && 'points' in objet) {
      const charge = op.charge as RealisationTournee;
      objet.realisation = { id: op.id_client, etat: charge.etat, date: charge.date, origine: 'synchronisation' };
    } else if (op.type === 'constat' && 'formulaire' in objet) {
      const charge = op.charge as SaisieConstat;
      if (charge.reponses.length > 0) objet.etat = 'constat_recu';
    }
  }

  async envoyerPhoto(p: EnvoiPhoto): Promise<ReponsePhotoTerrain> {
    await this.requete();
    const e = this.lire();
    const deja = e.photos[p.id_client];
    if (deja) return { ...deja, resultat: 'ignoree_doublon' };
    if (!e.journal[p.objet_id_client]) throw new ErreurApi(409, 'operation_inconnue', "La photo se rattache à une opération que le serveur n'a pas reçue.");
    const r: ReponsePhotoTerrain = { id_client: p.id_client, document_id: p.id_client, resultat: 'appliquee' };
    e.photos[p.id_client] = r;
    e.evenements.push(`${this.horloge().toISOString()} photo ${p.id_client} (${p.empreinte_sha256.slice(0, 12)}) : appliquee`);
    this.ecrire();
    return r;
  }

  /** Recette R9 : le responsable réaffecte la mission pendant la déconnexion (version +1, D-130). */
  reaffecter(missionId: string, equipe = 'Équipe voirie Nord'): MissionTerrain | null {
    const e = this.lire();
    const m = e.missions.find((x) => x.id === missionId);
    if (!m) return null;
    m.version += 1;
    m.equipe = { id: `eq-${equipe.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`, nom: equipe, type: 'municipale' };
    m.modifiee_le = this.horloge().toISOString();
    e.modifications[missionId] = `Mission réaffectée à ${equipe} par le responsable technique`;
    e.evenements.push(`${this.horloge().toISOString()} réaffectation ${missionId} vers ${equipe} (version ${m.version})`);
    this.ecrire();
    return copie(m);
  }

  cloturer(missionId: string): MissionTerrain | null {
    const e = this.lire();
    const m = e.missions.find((x) => x.id === missionId);
    if (!m) return null;
    m.version += 1;
    m.etat = 'validee';
    e.modifications[missionId] = 'Mission clôturée par le responsable technique';
    this.ecrire();
    return copie(m);
  }
}

let instance: SimulateurTerrain | null = null;

/** Simulateur partagé de la page ; exposé sur window.ccSimulateurTerrain pour les parcours de recette. */
export function simulateurPartage(): SimulateurTerrain {
  if (!instance) {
    instance = new SimulateurTerrain();
    if (typeof window !== 'undefined') (window as unknown as { ccSimulateurTerrain: SimulateurTerrain }).ccSimulateurTerrain = instance;
  }
  return instance;
}
